import { useCallback, useState } from 'react';
import type { RefObject, KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useScrollActiveIntoView } from './useScrollActiveIntoView';

interface UseRovingActiveIndexOptions {
    itemCount: number;
    /** 'clamp' stops at the ends (listbox/combobox); 'circular' wraps (menu). */
    wrap?: 'clamp' | 'circular';
    listRef: RefObject<HTMLElement | null>;
    onSelect: (index: number) => void;
    /**
     * Jump to first/last on Home/End (default true). Editable comboboxes set this
     * false so Home/End move the text cursor inside the input instead.
     */
    homeEnd?: boolean;
    /**
     * Select the active item on Space (default true). Editable comboboxes set this
     * false so Space types a literal space in the input.
     */
    selectOnSpace?: boolean;
}

/**
 * Single source of truth for Arrow/Home/End/Enter index math in keyboard-navigable
 * dropdowns — replaces the per-surface duplicated handlers. Tracks a virtual
 * "active" index (drive `aria-activedescendant` or roving tabindex from it) and
 * returns an `onKeyDown` to spread onto the focused element. Escape is owned by
 * `useDismiss`, so it is intentionally not handled here.
 */
export function useRovingActiveIndex({ itemCount, wrap = 'clamp', listRef, onSelect, homeEnd = true, selectOnSpace = true }: UseRovingActiveIndexOptions) {
    const [activeIndex, setActiveIndex] = useState(-1);
    useScrollActiveIntoView(activeIndex, listRef);

    const move = useCallback((next: number) => {
        if (itemCount <= 0) { setActiveIndex(-1); return; }
        const i = wrap === 'circular'
            ? (next + itemCount) % itemCount
            : Math.max(0, Math.min(itemCount - 1, next));
        setActiveIndex(i);
    }, [itemCount, wrap]);

    const onKeyDown = useCallback((e: ReactKeyboardEvent) => {
        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault();
                move(activeIndex < 0 ? 0 : activeIndex + 1);
                break;
            case 'ArrowUp':
                e.preventDefault();
                move(activeIndex < 0 ? itemCount - 1 : activeIndex - 1);
                break;
            case 'Home':
                if (!homeEnd) break;
                e.preventDefault();
                move(0);
                break;
            case 'End':
                if (!homeEnd) break;
                e.preventDefault();
                move(itemCount - 1);
                break;
            case ' ':
                // Editable comboboxes (selectOnSpace=false) let Space type a literal space.
                if (selectOnSpace && activeIndex >= 0) { e.preventDefault(); onSelect(activeIndex); }
                break;
            case 'Enter':
                if (activeIndex >= 0) { e.preventDefault(); onSelect(activeIndex); }
                break;
            default:
                break;
        }
    }, [activeIndex, itemCount, move, onSelect, homeEnd, selectOnSpace]);

    const reset = useCallback(() => setActiveIndex(-1), []);

    return { activeIndex, setActiveIndex, onKeyDown, reset };
}
